import React, { type FC } from "react";
import classNames from "classnames";
import type { StockObj } from "@src/api/stock/stockObjs";
import "./BusinessStockObjValue.scss";

/** Минимальный контракт ТМЦ для отображения. */
export type BusinessStockObjValueStockObj = Pick<StockObj, "Sign" | "Name" | "NomNum">;

/**
 * Режим раскладки {@link BusinessStockObjValue}.
 * - `oneLine` — компактное однострочное отображение с обрезкой длинных частей.
 * - `wrap` — перенос частей на несколько строк для узких контейнеров.
 */
export type BusinessStockObjValueMode = "oneLine" | "wrap";

/** Пропсы компонента {@link BusinessStockObjValue}. */
export interface BusinessStockObjValueProps {
    /** ТМЦ для read-only отображения. */
    stockObj?: BusinessStockObjValueStockObj | null;
    /** Текст или узел для пустого значения. @default "Не задано" */
    fallback?: React.ReactNode;
    /**
     * Режим раскладки значения. \
     * Используйте `wrap` в узких карточках, где важнее показать наименование полностью.
     * @default "oneLine"
     */
    mode?: BusinessStockObjValueMode;
    /** Дополнительный CSS-класс корневого контейнера. */
    className?: string;
}

function resolveTextPart(value: string | null | undefined): string | null {
    const text = value?.trim();
    return text === "" ? null : (text ?? null);
}

/**
 * Формирует plain-text представление ТМЦ.
 * @param stockObj ТМЦ
 * @returns строка вида `Обозначение - Наименование НомНомер` или пустая строка
 */
export function formatStockObjText(stockObj?: BusinessStockObjValueStockObj | null): string {
    if (stockObj == null) {
        return "";
    }

    const sign = resolveTextPart(stockObj.Sign);
    const name = resolveTextPart(stockObj.Name);
    const nomNum = resolveTextPart(stockObj.NomNum);
    const signWithName = [sign, name].filter((part) => part != null).join(" - ");

    return [signWithName, nomNum].filter((part) => part != null && part !== "").join(" ");
}

/**
 * Read-only отображение ТМЦ. \
 * `Sign`, `Name` и `NomNum` рендерятся отдельными частями для единообразной типографики.
 */
export const BusinessStockObjValue: FC<BusinessStockObjValueProps> = ({
    stockObj,
    fallback = "Не задано",
    mode = "oneLine",
    className,
}) => {
    if (stockObj == null) {
        return <span className={className}>{fallback}</span>;
    }

    const sign = resolveTextPart(stockObj.Sign);
    const name = resolveTextPart(stockObj.Name);
    const nomNum = resolveTextPart(stockObj.NomNum);

    if (sign == null && name == null && nomNum == null) {
        return <span className={className}>{fallback}</span>;
    }

    return (
        <span
            className={classNames(
                "business_stock_obj_value",
                `business_stock_obj_value--${mode === "oneLine" ? "one_line" : "wrap"}`,
                className,
            )}
        >
            {sign != null && <span className="business_stock_obj_value__sign">{sign}</span>}
            {name != null && <span className="business_stock_obj_value__name">{name}</span>}
            {nomNum != null && <span className="business_stock_obj_value__nom_num">{nomNum}</span>}
        </span>
    );
};
